<template>
  <div class="sms-client">
    <!-- Loading state -->
    <div class="loading-state" v-if="loading && !dataReady">
      <div class="loading-spinner"></div>
      <span class="loading-text">Chargement...</span>
    </div>

    <div class="page" v-if="dataReady">
      <!-- Page header -->
      <div class="page-header">
        <div class="page-title-group">
          <div class="page-icon">
            <span class="material-symbols-outlined">schedule</span>
          </div>
          <div>
            <h1 class="page-title">Plages horaires SMS</h1>
            <p class="page-subtitle">{{ client.name }} — {{ clientUsers.length }} utilisateur{{ clientUsers.length > 1 ? 's' : '' }}</p>
          </div>
        </div>
        <button class="btn-refresh" @click="refresh" :disabled="loading">
          <span class="material-symbols-outlined" :class="{ spinning: loading }">sync</span>
        </button>
      </div>

      <!-- Users list card -->
      <div class="users-card">
        <!-- Empty state -->
        <div class="empty" v-if="clientUsers.length === 0">
          <span class="material-symbols-outlined empty-icon">person_off</span>
          <span class="empty-text">Aucun utilisateur avec acces SMS</span>
        </div>

        <!-- User rows -->
        <div
          class="user-row"
          v-for="user in clientUsers"
          :key="user.id"
        >
          <div class="user-identity">
            <div class="avatar">{{ getUserInitials(user) }}</div>
            <div class="user-text">
              <span class="name">{{ user.firstname }} {{ user.lastname }}</span>
              <span class="email">{{ user.email }}</span>
            </div>
          </div>

          <div class="time-range">
            <div class="time-slot">
              <label>Debut</label>
              <div class="time-input">
                <span class="material-symbols-outlined time-icon">wb_sunny</span>
                <input
                  type="time"
                  :value="getUserConfig(user.id).startHour"
                  @change="updateUserConfig(user.id, 'startHour', $event.target.value)"
                  :disabled="savingStates[user.id]"
                />
              </div>
            </div>

            <span class="range-arrow">&rarr;</span>

            <div class="time-slot">
              <label>Fin</label>
              <div class="time-input">
                <span class="material-symbols-outlined time-icon">nights_stay</span>
                <input
                  type="time"
                  :value="getUserConfig(user.id).endHour"
                  @change="updateUserConfig(user.id, 'endHour', $event.target.value)"
                  :disabled="savingStates[user.id]"
                />
              </div>
            </div>
          </div>

          <div class="row-actions">
            <button
              class="action-btn save"
              @click.stop="saveUserConfig(user.id)"
              :disabled="savingStates[user.id] || !hasUnsavedChanges(user.id)"
            >
              <span class="material-symbols-outlined" :class="{ spinning: savingStates[user.id] }">
                {{ savingStates[user.id] ? 'sync' : 'save' }}
              </span>
            </button>
            <button
              class="action-btn reset"
              @click.stop="resetUserConfig(user.id)"
              :disabled="savingStates[user.id]"
            >
              <span class="material-symbols-outlined">undo</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toasts -->
    <transition-group name="toast" tag="div" class="toast-container">
      <div class="toast" v-for="toast in toasts" :key="toast.id" :class="toast.type">
        <span class="material-symbols-outlined">{{ toast.icon }}</span>
        <span class="toast-message">{{ toast.message }}</span>
        <button class="toast-close" @click="removeToast(toast.id)">
          <span class="material-symbols-outlined">close</span>
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: 'SmsManagementClient',
  data() {
    return {
      profile: this.$store.state.profile.user,
      loading: false,
      dataReady: false,
      userConfigs: {},
      originalConfigs: {},
      smsStats: {},
      savingStates: {},
      toasts: []
    };
  },

  computed: {
    /**
     * Auto-detect the client: first client from the user's profile.
     */
    client() {
      return (this.profile.clients || [])[0] || { id: null, name: '' };
    },

    clientSites() {
      if (!this.client.id) return [];
      return (this.profile.sites || []).filter((site) => site.client_id === this.client.id);
    },

    clientUsers() {
      const map = new Map();

      this.clientSites.forEach((site) => {
        (site.users || []).forEach((user) => {
          if (!map.has(user.id) && user.role != 'Admin') {
            map.set(user.id, user);
          }
        });
      });

      return Array.from(map.values()).filter((user) => user.optin_sms == 1);
    }
  },

  mounted() {
    this.loadData();
  },

  methods: {
    async loadData() {
      this.loading = true;

      try {
        await this.$store.dispatch('profileRequest');
        this.profile = this.$store.state.profile.user;

        this.initializeConfigs();
        this.dataReady = true;
      } catch (e) {
        console.error('Error:', e);
        this.showToast('error', 'Erreur lors du chargement');
      } finally {
        this.loading = false;
      }
    },

    initializeConfigs() {
      const clientId = this.client.id;
      if (!clientId) return;

      const handled = new Set();

      this.clientUsers.forEach((user) => {
        if (handled.has(user.id)) return;
        handled.add(user.id);

        const existing =
          user.sms_config ||
          user.sms_configs?.[clientId] ||
          user.sms_config_by_client?.[clientId] ||
          {};

        this.$set(this.userConfigs, user.id, {
          startHour: existing.start_hour || existing.startHour || '06:00',
          endHour: existing.end_hour || existing.endHour || '22:00'
        });

        this.$set(this.originalConfigs, user.id, {
          startHour: existing.start_hour || existing.startHour || '06:00',
          endHour: existing.end_hour || existing.endHour || '22:00'
        });

        this.$set(this.smsStats, user.id, user.sms_count || 0);
      });

      this.clientSites.forEach((site) => {
        this.$set(this.smsStats, `site-${site.id}`, site.sms_count || 0);
      });
    },

    refresh() {
      this.dataReady = false;
      this.userConfigs = {};
      this.originalConfigs = {};
      this.smsStats = {};
      this.loadData();
    },

    getUserInitials(user) {
      return `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}`.toUpperCase() || '?';
    },

    getUserConfig(userId) {
      return this.userConfigs[userId] || { startHour: '', endHour: '' };
    },

    updateUserConfig(userId, field, value) {
      if (!this.userConfigs[userId]) {
        this.$set(this.userConfigs, userId, { startHour: '', endHour: '' });
      }

      this.$set(this.userConfigs[userId], field, value);
    },

    hasUnsavedChanges(userId) {
      const current = this.userConfigs[userId] || {};
      const original = this.originalConfigs[userId] || {};

      return current.startHour !== original.startHour || current.endHour !== original.endHour;
    },

    async saveUserConfig(userId) {
      this.$set(this.savingStates, userId, true);

      try {
        const config = this.userConfigs[userId];

        await this.$store.dispatch('updateUserSmsConfig', {
          userId,
          startHour: config.startHour,
          endHour: config.endHour
        });

        this.$set(this.originalConfigs, userId, { ...config });
        this.showToast('success', 'Configuration sauvegardee');
      } catch (e) {
        console.error('Error:', e);
        this.showToast('error', 'Erreur lors de la sauvegarde');
      } finally {
        this.$set(this.savingStates, userId, false);
      }
    },

    resetUserConfig(userId) {
      const original = this.originalConfigs[userId] || { startHour: '', endHour: '' };
      this.$set(this.userConfigs, userId, { ...original });
      this.showToast('info', 'Configuration reinitialisee');
    },

    showToast(type, message) {
      const icons = {
        success: 'check_circle',
        error: 'error',
        warning: 'warning',
        info: 'info'
      };

      const toast = {
        id: `${Date.now()}-${Math.random()}`,
        type,
        message,
        icon: icons[type] || 'info'
      };

      this.toasts.push(toast);
      setTimeout(() => this.removeToast(toast.id), 4000);
    },

    removeToast(id) {
      const index = this.toasts.findIndex((toast) => toast.id === id);
      if (index > -1) this.toasts.splice(index, 1);
    }
  }
};
</script>

<style scoped>
.sms-client {
  --primary: #2d5a3d;
  --primary-light: #3d7a52;
  --primary-bg: #e8f0eb;
  --surface: #ffffff;
  --bg: #f5f7f6;
  --border: #e2e8e4;
  --border-light: #eef2ef;
  --text: #1a2e1f;
  --text-secondary: #4a5d4f;
  --text-muted: #8a998e;
  --success: #059669;
  --success-bg: #ecfdf5;
  --danger: #dc2626;
  --info: #0284c7;
  --info-bg: #e0f2fe;
  --radius: 14px;
  --radius-sm: 8px;
  --shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.03);

  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
  color: var(--text);
  overflow-y: auto;
}

/* -- Loading -- */
.loading-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  font-size: 0.9375rem;
  color: var(--text-muted);
  font-weight: 500;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* -- Page -- */
.page {
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 40px 32px 64px;
}

/* -- Page header -- */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 32px;
}

.page-title-group {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-light) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-icon .material-symbols-outlined {
  font-size: 22px;
  color: #ffffff;
}

.page-title {
  font-size: 1.375rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--text);
  margin: 0;
  line-height: 1.3;
}

.page-subtitle {
  font-size: 0.8125rem;
  color: var(--text-muted);
  margin: 4px 0 0;
  line-height: 1.4;
}

.btn-refresh {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text-secondary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: var(--primary-bg);
  color: var(--primary);
  border-color: var(--primary-light);
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-refresh .material-symbols-outlined {
  font-size: 20px;
}

.spinning {
  animation: spin 1s linear infinite;
}

/* -- Users card -- */
.users-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: var(--shadow);
}

/* -- Empty state -- */
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  gap: 12px;
}

.empty-icon {
  font-size: 48px;
  color: var(--border);
}

.empty-text {
  font-size: 0.9375rem;
  color: var(--text-muted);
}

/* -- User row -- */
.user-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-light);
  transition: background-color 0.15s ease;
  gap: 20px;
}

.user-row:last-child {
  border-bottom: none;
}

.user-row:hover {
  background: #fafcfb;
}

/* -- User identity -- */
.user-identity {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 180px;
}

.avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--primary-bg);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 600;
  flex-shrink: 0;
  letter-spacing: 0.02em;
}

.user-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-text .name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text);
  line-height: 1.3;
}

.user-text .email {
  font-size: 0.75rem;
  color: var(--text-muted);
  line-height: 1.3;
}

/* -- Time range -- */
.time-range {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-slot {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.time-slot label {
  font-size: 0.6875rem;
  text-transform: uppercase;
  color: var(--text-muted);
  letter-spacing: 0.05em;
  font-weight: 500;
}

.time-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8faf9;
  border: 1px solid var(--border);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.time-input:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--primary-bg);
}

.time-icon {
  font-size: 18px;
  color: var(--text-muted);
}

.time-input input {
  border: none;
  background: transparent;
  font-size: 0.875rem;
  font-family: 'SF Mono', 'Roboto Mono', 'Consolas', monospace;
  color: var(--text);
  outline: none;
  width: 76px;
  line-height: 1.4;
}

.time-input input:disabled {
  opacity: 0.5;
}

.range-arrow {
  color: var(--text-muted);
  padding-top: 20px;
  font-size: 1.125rem;
  user-select: none;
}

/* -- Row actions -- */
.row-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.action-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.15s ease;
}

.action-btn .material-symbols-outlined {
  font-size: 18px;
}

.action-btn.save {
  background: var(--primary);
  color: #ffffff;
}

.action-btn.save:hover:not(:disabled) {
  background: var(--primary-light);
}

.action-btn.save:disabled {
  background: var(--border);
  color: var(--text-muted);
  cursor: not-allowed;
}

.action-btn.reset {
  background: transparent;
  color: var(--text-muted);
  border: 1px solid var(--border);
}

.action-btn.reset:hover:not(:disabled) {
  background: #f8faf9;
  color: var(--text-secondary);
  border-color: var(--text-muted);
}

.action-btn.reset:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* -- Toasts -- */
.toast-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  z-index: 1000;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: var(--surface);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1), 0 1px 4px rgba(0, 0, 0, 0.06);
  min-width: 280px;
  max-width: 400px;
}

.toast.success {
  border-left: 4px solid var(--success);
}

.toast.error {
  border-left: 4px solid var(--danger);
}

.toast.warning {
  border-left: 4px solid #d97706;
}

.toast.info {
  border-left: 4px solid var(--info);
}

.toast .material-symbols-outlined {
  font-size: 22px;
}

.toast.success .material-symbols-outlined {
  color: var(--success);
}

.toast.error .material-symbols-outlined {
  color: var(--danger);
}

.toast.warning .material-symbols-outlined {
  color: #d97706;
}

.toast.info .material-symbols-outlined {
  color: var(--info);
}

.toast-message {
  flex: 1;
  font-size: 0.875rem;
  color: var(--text);
}

.toast-close {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--text-muted);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.toast-close:hover {
  background: #f0f4f2;
  color: var(--text);
}

.toast-close .material-symbols-outlined {
  font-size: 18px;
}

/* -- Transitions -- */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* -- Responsive -- */
@media (max-width: 768px) {
  .page {
    padding: 24px 16px 48px;
  }

  .page-header {
    padding-bottom: 24px;
  }

  .user-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 16px 20px;
  }

  .user-identity {
    min-width: unset;
  }

  .time-range {
    width: 100%;
  }

  .row-actions {
    align-self: flex-end;
  }
}

@media (max-width: 480px) {
  .page {
    padding: 16px 12px 40px;
  }

  .time-range {
    flex-wrap: wrap;
    gap: 8px;
  }

  .range-arrow {
    display: none;
  }

  .time-slot {
    flex: 1;
    min-width: 120px;
  }
}

.material-symbols-outlined {
  font-family: 'Material Symbols Outlined';
  font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}
</style>
